import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from 'store';

import {
  NotificationsCountResponse,
  NotificationsListResponse
} from 'services/notifications/notifications.types';

import {
  DateGroupedNotifications,
  NotificationsState,
  RealtimeUpsertPayload,
  SetResolvedStatePayload
} from './notifications.types';
import { groupByDate, sortNotifications } from './notifications.settings';

const createListInitialState = (): Pick<
  NotificationsState,
  | 'hasMore'
  | 'isError'
  | 'isLoading'
  | 'isLoadingMore'
  | 'notifications'
  | 'paginatedCount'
  | 'socketCreatedIds'
  | 'totalCount'
> => ({
  hasMore: false,
  isError: false,
  isLoading: false,
  isLoadingMore: false,
  notifications: [],
  paginatedCount: 0,
  socketCreatedIds: [],
  totalCount: 0
});

const initialState: Readonly<NotificationsState> = {
  ...createListInitialState(),
  hasUnreadNotifications: false,
  highPriorityUnreadCount: 0,
  shouldAnimateIndicator: false
};

const notificationsSlice = createSlice({
  initialState,
  name: 'notifications',
  reducers: {
    appendNotifications: (state, action: PayloadAction<NotificationsListResponse>) => {
      const { data, info } = action.payload;

      // paginatedCount tracks the next backend offset to request, so it advances
      // by the response length even when items are deduplicated locally.
      state.paginatedCount += data.length;

      if (data.length === 0) {
        state.hasMore = false;
        state.isLoadingMore = false;
        state.isError = false;
        return;
      }

      // Any socket-created item that appears in this paginated response is now
      // confirmed at a position within the paginated prefix; demote it so that a
      // future deletion correctly decrements paginatedCount.
      if (state.socketCreatedIds.length > 0) {
        const responseIds = new Set(data.map((n) => n._id));
        state.socketCreatedIds = state.socketCreatedIds.filter((id) => !responseIds.has(id));
      }

      const existingIds = new Set(state.notifications.map((n) => n._id));
      const deduped = data.filter((n) => !existingIds.has(n._id));
      state.notifications = [...state.notifications, ...deduped];
      state.totalCount = info.totalCount;
      state.hasMore = state.notifications.length < info.totalCount;
      state.isLoadingMore = false;
      state.isError = false;
    },
    markAsSeen: (state, action: PayloadAction<string[]>) => {
      const ids = new Set(action.payload);
      state.notifications = state.notifications.map((n) =>
        ids.has(n._id) ? { ...n, seenAt: new Date().toISOString() } : n
      );
    },
    removeNotifications: (state, action: PayloadAction<string[]>) => {
      const ids = new Set(action.payload);
      const socketSet = new Set(state.socketCreatedIds);
      let removedPaginated = 0;

      state.notifications = state.notifications.filter((n) => {
        if (ids.has(n._id)) {
          // Only paginated items shift the backend prefix; socket-created items
          // that haven't been paginated yet sit outside paginatedCount's range.
          if (!socketSet.has(n._id)) removedPaginated++;
          return false;
        }
        return true;
      });

      state.socketCreatedIds = state.socketCreatedIds.filter((id) => !ids.has(id));
      // totalCount intentionally not adjusted here; the next loadMore response
      // resyncs it. Created events still increment it synchronously so hasMore
      // doesn't lock false at the boundary.
      state.paginatedCount = Math.max(0, state.paginatedCount - removedPaginated);
      state.hasMore = state.notifications.length < state.totalCount;
    },
    resetNotificationsList: (state) => {
      Object.assign(state, createListInitialState());
    },
    setError: (state, action: PayloadAction<boolean>) => {
      state.isError = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setLoadingMore: (state, action: PayloadAction<boolean>) => {
      state.isLoadingMore = action.payload;
    },
    setNotifications: (state, action: PayloadAction<NotificationsListResponse>) => {
      const { data, info } = action.payload;
      state.notifications = [...data];
      state.totalCount = info?.totalCount;
      state.hasMore = data.length < info?.totalCount;
      state.paginatedCount = data.length;
      state.socketCreatedIds = [];
      state.isLoading = false;
      state.isError = false;
    },
    setResolvedState: (state, action: PayloadAction<SetResolvedStatePayload>) => {
      state.notifications = state.notifications.map((n) =>
        n._id === action.payload.id ? { ...n, resolvedState: action.payload.resolvedState } : n
      );
    },
    setUnreadStatus: (state, action: PayloadAction<NotificationsCountResponse>) => {
      state.hasUnreadNotifications = action.payload.hasUnreadNotifications;
      state.highPriorityUnreadCount = action.payload.highPriorityUnreadNotificationsCount;
    },
    triggerIndicatorAnimation: (state, action: PayloadAction<boolean>) => {
      state.shouldAnimateIndicator = action.payload;
    },
    // Realtime path. Known limitation: when a socket "updated" event changes an
    // item's priority such that it crosses the loaded-window boundary, the
    // pagination invariants in this slice cannot detect the move and the user
    // may see a duplicate or miss an item until refresh. Cursor-based pagination
    // had the same class of bug; fixing it requires refetching the first page on
    // priority-changing updates and is intentionally out of scope here.
    upsertNotifications: (state, action: PayloadAction<RealtimeUpsertPayload>) => {
      const { created, updated } = action.payload;

      // Apply updates in place: replace by _id where present, drop the rest.
      // An update for an out-of-window id should not pop into view.
      if (updated.length > 0) {
        const updatedMap = new Map(updated.map((n) => [n._id, n]));
        state.notifications = state.notifications.map((n) => updatedMap.get(n._id) ?? n);
      }

      // Apply created: prepend ids new to state, replace in place for any
      // already present (defends against socket replay / out-of-order delivery).
      if (created.length > 0) {
        const existingIds = new Set(state.notifications.map((n) => n._id));
        const trulyNew = created.filter((n) => !existingIds.has(n._id));

        if (trulyNew.length !== created.length) {
          const replayMap = new Map(
            created.filter((n) => existingIds.has(n._id)).map((n) => [n._id, n])
          );
          state.notifications = state.notifications.map((n) => replayMap.get(n._id) ?? n);
        }

        if (trulyNew.length > 0) {
          state.notifications = [...trulyNew, ...state.notifications];
          state.totalCount += trulyNew.length;
          state.socketCreatedIds = [...state.socketCreatedIds, ...trulyNew.map((n) => n._id)];
        }
      }
    }
  }
});

export const {
  appendNotifications,
  markAsSeen,
  removeNotifications,
  resetNotificationsList,
  setError,
  setLoading,
  setLoadingMore,
  setNotifications,
  setResolvedState,
  setUnreadStatus,
  triggerIndicatorAnimation,
  upsertNotifications
} = notificationsSlice.actions;

export const selectHasUnreadNotifications = (state: RootState) =>
  state.notifications.hasUnreadNotifications;
export const selectHighPriorityUnreadCount = (state: RootState) =>
  state.notifications.highPriorityUnreadCount;
export const selectNotificationsList = (state: RootState) => state.notifications.notifications;
export const selectShouldAnimateIndicator = (state: RootState) =>
  state.notifications.shouldAnimateIndicator;
export const selectHasMore = (state: RootState) => state.notifications.hasMore;
export const selectIsError = (state: RootState) => state.notifications.isError;
export const selectPaginatedCount = (state: RootState) => state.notifications.paginatedCount;
export const selectTotalCount = (state: RootState) => state.notifications.totalCount;
export const selectIsLoading = (state: RootState) => state.notifications.isLoading;
export const selectIsLoadingMore = (state: RootState) => state.notifications.isLoadingMore;

export const selectGroupedNotifications = createSelector(
  [selectNotificationsList],
  (notifications): DateGroupedNotifications => groupByDate(sortNotifications(notifications))
);

export default notificationsSlice.reducer;
